import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { AlertCircle, ChevronRight, Plus, Receipt, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import {
  API, authHeaders, DhSymbol, fmtAmount, todayIso, brandMeta,
  periodLabelFromDate, addMonthsIso, blankClient, emptyItem,
} from './utils';

const CreateInvoiceDialog = ({ open, onOpenChange, clients, brands = [], onClientCreated, editing = null, repeating = null }) => {
  const [brand, setBrand] = useState('vynce');
  const [mode, setMode] = useState('saved'); // saved | adhoc
  const [clientId, setClientId] = useState('');
  const [adhoc, setAdhoc] = useState(blankClient);
  const [periodType, setPeriodType] = useState('monthly');
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [periodLabel, setPeriodLabel] = useState(periodLabelFromDate(todayIso(), 'monthly'));
  const [items, setItems] = useState([emptyItem()]);
  const [taxPercent, setTaxPercent] = useState(0);
  const [taxMode, setTaxMode] = useState('exclusive'); // exclusive | inclusive
  const [tips, setTips] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Payment due within 30 days. Bank transfer preferred.');
  const [includeStamp, setIncludeStamp] = useState(true);
  const [busy, setBusy] = useState(false);

  const isEdit = !!editing;
  const isRepeat = !isEdit && !!repeating;

  // Stamp default per brand: Vynce ON, Stars n Bars OFF (stamp optional)
  const pickBrand = (b) => { setBrand(b); setIncludeStamp(b === 'vynce'); };

  useEffect(() => {
    if (!open) return;
    const source = editing || repeating;
    if (source) {
      // Pre-fill from existing invoice (edit = same invoice, repeat = next month)
      setBrand(source.brand || 'vynce');
      const cli = source.client || {};
      // Match against saved clients by id
      const savedMatch = clients.find(c => c.id === cli.id);
      if (savedMatch) {
        setMode('saved'); setClientId(savedMatch.id); setAdhoc(blankClient);
      } else {
        setMode('adhoc');
        setClientId('');
        setAdhoc({
          name: cli.name || '',
          contact_person: cli.contact_person || '',
          email: cli.email || '',
          phone: cli.phone || '',
          address: cli.address || '',
          trn: cli.trn || '',
        });
      }
      const pType = source.period_type || 'monthly';
      setPeriodType(pType);
      if (repeating) {
        // Roll forward one month for the next billing cycle
        const nextDate = addMonthsIso(source.invoice_date, 1);
        setInvoiceDate(nextDate);
        setDueDate(source.due_date ? addMonthsIso(source.due_date, 1) : '');
        setPeriodLabel(
          (pType === 'custom' || pType === 'none')
            ? (source.period_label || '')
            : periodLabelFromDate(nextDate, pType)
        );
      } else {
        setInvoiceDate(source.invoice_date || todayIso());
        setDueDate(source.due_date || '');
        setPeriodLabel(source.period_label || '');
      }
      setItems((source.items && source.items.length) ? source.items.map(it => ({
        description: it.description || '',
        quantity: it.quantity ?? 1,
        unit_price: it.unit_price ?? 0,
      })) : [emptyItem()]);
      setTaxPercent(source.tax_percent || 0);
      setTaxMode(source.tax_mode || 'exclusive');
      setTips(source.tips || 0);
      setDiscountPercent(source.discount_percent || 0);
      setNotes(source.notes || '');
      setTerms(source.terms || '');
      setIncludeStamp(source.include_stamp !== false);
    } else {
      // Fresh create
      setBrand('vynce');
      setIncludeStamp(true);
      setMode(clients.length ? 'saved' : 'adhoc');
      setClientId(clients[0]?.id || '');
      setAdhoc(blankClient);
      setPeriodType('monthly');
      setInvoiceDate(todayIso());
      setDueDate('');
      setPeriodLabel(periodLabelFromDate(todayIso(), 'monthly'));
      setItems([emptyItem()]);
      setTaxPercent(0);
      setTaxMode('exclusive');
      setTips(0);
      setDiscountPercent(0);
      setNotes('');
      setTerms('Payment due within 30 days. Bank transfer preferred.');
    }
  }, [open, clients, editing, repeating]);

  // Auto-refresh period label when date or type changes (skip while editing untouched)
  useEffect(() => {
    if (periodType === 'none') { setPeriodLabel(''); return; }
    if (periodType !== 'custom' && !isEdit) {
      setPeriodLabel(periodLabelFromDate(invoiceDate, periodType));
    }
  }, [invoiceDate, periodType, isEdit]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const discount = subtotal * (Number(discountPercent) || 0) / 100;
    const afterDisc = subtotal - discount;
    const rate = Number(taxPercent) || 0;
    const tipsAmt = Number(tips) || 0;
    let tax, taxNet, grand;
    if (taxMode === 'inclusive') {
      tax = afterDisc * rate / 100;
      taxNet = afterDisc - tax;      // e.g. 100 @ 5% -> net 95
      grand = afterDisc + tipsAmt;   // VAT already inside the total
    } else {
      tax = afterDisc * rate / 100;
      taxNet = afterDisc;
      grand = afterDisc + tax + tipsAmt;
    }
    return {
      subtotal: +subtotal.toFixed(2),
      discount: +discount.toFixed(2),
      tax: +tax.toFixed(2),
      taxNet: +taxNet.toFixed(2),
      tips: +tipsAmt.toFixed(2),
      grand: +grand.toFixed(2),
    };
  }, [items, taxPercent, taxMode, tips, discountPercent]);

  const updateItem = (idx, patch) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const submit = async () => {
    // Validation
    if (mode === 'saved' && !clientId) {
      toast({ title: 'Pick a client', variant: 'destructive' }); return;
    }
    if (mode === 'adhoc' && !adhoc.name.trim()) {
      toast({ title: 'Client name is required', variant: 'destructive' }); return;
    }
    if (!items.some(it => it.description.trim())) {
      toast({ title: 'Add at least one line item with a description', variant: 'destructive' }); return;
    }
    const cleanItems = items
      .filter(it => it.description.trim() || Number(it.unit_price) > 0)
      .map(it => ({
        description: it.description.trim(),
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
      }));

    setBusy(true);
    try {
      const payload = {
        brand,
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        period_type: periodType,
        period_label: periodLabel,
        include_stamp: includeStamp,
        items: cleanItems,
        tax_percent: Number(taxPercent) || 0,
        tax_mode: taxMode,
        discount_percent: Number(discountPercent) || 0,
        tips: Number(tips) || 0,
        notes,
        terms,
      };
      if (mode === 'saved') payload.client_id = clientId;
      else payload.client_snapshot = adhoc;

      if (isEdit) {
        const r = await axios.put(`${API}/api/invoices/${editing.id}`, payload, { headers: authHeaders() });
        toast({ title: 'Invoice updated', description: `${r.data.invoice_number} · Dh${fmtAmount(r.data.totals.grand_total)}` });
      } else {
        const r = await axios.post(`${API}/api/invoices`, payload, { headers: authHeaders() });
        toast({ title: 'Invoice created', description: `${r.data.invoice_number} · Dh${fmtAmount(r.data.totals.grand_total)}` });
      }
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Failed to create',
        description: e?.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-rose-600" />
            {isEdit ? `Edit Invoice ${editing.invoice_number}` : isRepeat ? `Repeat Invoice — next cycle` : 'Generate New Invoice'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isRepeat && repeating && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 flex items-start gap-2" data-testid="repeat-hint">
              <Receipt className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Repeating <b>{repeating.invoice_number}</b> for the next billing cycle. Date rolled forward one month — a brand-new invoice number will be assigned on save. Adjust anything before generating.
              </span>
            </div>
          )}
          {/* Brand / issuing company */}
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-sm">Issuing Company</p>
                  <p className="text-xs text-gray-500">
                    {isEdit
                      ? 'Brand is fixed once an invoice number is assigned.'
                      : 'Each brand has its own reference-number sequence & letterhead.'}
                  </p>
                </div>
                {isEdit ? (
                  <Badge variant="outline" className={brandMeta(brand).cls} data-testid="invoice-brand-badge">
                    {brandMeta(brand).label}
                  </Badge>
                ) : (
                  <div className="inline-flex bg-gray-100 rounded-lg p-0.5 text-xs" data-testid="brand-select">
                    {(brands.length ? brands : [{ key: 'vynce', label: 'Vynce' }, { key: 'stars_n_bars', label: 'Stars N Bars' }]).map(b => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => pickBrand(b.key)}
                        className={`px-3 py-1.5 rounded-md font-medium transition ${brand === b.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        data-testid={`brand-option-${b.key}`}
                      >{b.label}</button>
                    ))}
                  </div>
                )}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="include-stamp-toggle">
                <input
                  type="checkbox"
                  checked={includeStamp}
                  onChange={e => setIncludeStamp(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="include-stamp-checkbox"
                />
                <span className="text-gray-700">Include company stamp &amp; signature on the PDF</span>
              </label>
            </CardContent>
          </Card>

          {/* Client */}
          <Card className="border-rose-100">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">Bill To</p>
                <div className="inline-flex bg-gray-100 rounded-lg p-0.5 text-xs">
                  <button
                    onClick={() => setMode('saved')}
                    className={`px-3 py-1 rounded-md ${mode === 'saved' ? 'bg-white shadow text-rose-700' : 'text-gray-500'}`}
                    data-testid="mode-saved"
                  >Saved client</button>
                  <button
                    onClick={() => setMode('adhoc')}
                    className={`px-3 py-1 rounded-md ${mode === 'adhoc' ? 'bg-white shadow text-rose-700' : 'text-gray-500'}`}
                    data-testid="mode-adhoc"
                  >Ad-hoc</button>
                </div>
              </div>

              {mode === 'saved' ? (
                clients.length === 0 ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    No saved clients yet. Switch to <b>Ad-hoc</b> or add a client first.
                  </div>
                ) : (
                  <Select value={clientId} onValueChange={setClientId}>
                    <SelectTrigger data-testid="select-client"><SelectValue placeholder="Choose a client…" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.contact_person ? ` — ${c.contact_person}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Client Name *</Label>
                    <Input value={adhoc.name} onChange={e => setAdhoc({ ...adhoc, name: e.target.value })} data-testid="adhoc-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Contact Person</Label>
                    <Input value={adhoc.contact_person} onChange={e => setAdhoc({ ...adhoc, contact_person: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input value={adhoc.email} onChange={e => setAdhoc({ ...adhoc, email: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={adhoc.phone} onChange={e => setAdhoc({ ...adhoc, phone: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Address</Label>
                    <Textarea rows={2} value={adhoc.address} onChange={e => setAdhoc({ ...adhoc, address: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">TRN</Label>
                    <Input value={adhoc.trn} onChange={e => setAdhoc({ ...adhoc, trn: e.target.value })} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Meta */}
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Invoice Date *</Label>
              <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="invoice-date" />
            </div>
            <div>
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Period</Label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger data-testid="period-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="none">None (no period)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Period Label</Label>
              <Input
                value={periodLabel}
                onChange={e => setPeriodLabel(e.target.value)}
                placeholder={periodType === 'none' ? 'Not applicable' : 'e.g. May 2026'}
                disabled={periodType === 'none'}
                data-testid="period-label"
              />
            </div>
          </div>

          {/* Line items */}
          <Card>
            <CardContent className="p-3 space-y-2">
              <div className="flex justify-between items-center">
                <p className="font-semibold text-sm">Line Items</p>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line-item">
                  <Plus className="h-4 w-4 mr-1" /> Add Row
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase text-gray-500">
                    <tr>
                      <th className="text-left py-1 pl-2">Description</th>
                      <th className="text-right py-1 w-20">Qty</th>
                      <th className="text-right py-1 w-32">Unit Price</th>
                      <th className="text-right py-1 w-32">Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                      return (
                        <tr key={idx} className="border-t">
                          <td className="py-1 pl-1 pr-2">
                            <Input
                              value={it.description}
                              onChange={e => updateItem(idx, { description: e.target.value })}
                              placeholder="What are you billing for?"
                              data-testid={`line-desc-${idx}`}
                            />
                          </td>
                          <td className="py-1 px-1">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.quantity}
                              onChange={e => updateItem(idx, { quantity: e.target.value })}
                              className="text-right"
                              data-testid={`line-qty-${idx}`}
                            />
                          </td>
                          <td className="py-1 px-1">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                                <DhSymbol />
                              </span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={it.unit_price}
                                onChange={e => updateItem(idx, { unit_price: e.target.value })}
                                className="text-right pl-8"
                                data-testid={`line-price-${idx}`}
                              />
                            </div>
                          </td>
                          <td className="py-1 px-1 text-right font-medium whitespace-nowrap">
                            <DhSymbol className="text-[10px] text-gray-400 mr-0.5" />{fmtAmount(lineTotal)}
                          </td>
                          <td className="py-1 px-1 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeItem(idx)}
                              disabled={items.length <= 1}
                              className="text-rose-500 h-7 w-7 p-0"
                              data-testid={`remove-line-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Tax / Discount / Totals */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Discount %</Label>
                    <Input
                      type="number" min="0" max="100" step="0.01"
                      value={discountPercent}
                      onChange={e => setDiscountPercent(e.target.value)}
                      data-testid="discount-percent"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">VAT / Tax %</Label>
                    <Input
                      type="number" min="0" max="100" step="0.01"
                      value={taxPercent}
                      onChange={e => setTaxPercent(e.target.value)}
                      placeholder="e.g. 5 for UAE VAT"
                      data-testid="tax-percent"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">VAT applies as</Label>
                  <Select value={taxMode} onValueChange={setTaxMode}>
                    <SelectTrigger data-testid="tax-mode" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exclusive">Added on top of total</SelectItem>
                      <SelectItem value="inclusive">Already included in total</SelectItem>
                    </SelectContent>
                  </Select>
                  {taxMode === 'inclusive' && Number(taxPercent) > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="tax-mode-hint">
                      VAT is extracted from the total (e.g. Dh100 @ {taxPercent}% → net Dh{fmtAmount(totals.taxNet)}, VAT Dh{fmtAmount(totals.tax)}).
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Tips (optional)</Label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                      <DhSymbol />
                    </span>
                    <Input
                      type="number" min="0" step="0.01"
                      value={tips}
                      onChange={e => setTips(e.target.value)}
                      placeholder="0.00"
                      className="pl-8"
                      data-testid="tips-amount"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Added to the grand total after VAT (not taxed).</p>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes printed on the invoice…" />
                </div>
                <div>
                  <Label className="text-xs">Terms &amp; Conditions</Label>
                  <Textarea rows={3} value={terms} onChange={e => setTerms(e.target.value)} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium"><DhSymbol className="text-[10px] mr-0.5 text-gray-400" />{fmtAmount(totals.subtotal)}</span>
                </div>
                {Number(discountPercent) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount ({discountPercent}%)</span>
                    <span className="font-medium text-rose-600">- <DhSymbol className="text-[10px] mx-0.5 text-rose-300" />{fmtAmount(totals.discount)}</span>
                  </div>
                )}
                {Number(taxPercent) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      VAT ({taxPercent}%){taxMode === 'inclusive' ? <span className="text-gray-400"> · incl. on {fmtAmount(totals.taxNet)}</span> : ''}
                    </span>
                    <span className="font-medium"><DhSymbol className="text-[10px] mr-0.5 text-gray-400" />{fmtAmount(totals.tax)}</span>
                  </div>
                )}
                {Number(tips) > 0 && (
                  <div className="flex justify-between text-sm" data-testid="tips-row">
                    <span className="text-gray-600">Tips</span>
                    <span className="font-medium"><DhSymbol className="text-[10px] mr-0.5 text-gray-400" />{fmtAmount(totals.tips)}</span>
                  </div>
                )}
                <div className="border-t border-rose-200 pt-2 flex justify-between items-baseline">
                  <span className="text-sm font-bold text-gray-900">GRAND TOTAL</span>
                  <span className="text-2xl font-bold text-rose-700" data-testid="grand-total">
                    <DhSymbol className="text-base mr-1" />{fmtAmount(totals.grand)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="submit-invoice"
          >
            {busy ? (isEdit ? 'Updating…' : 'Generating…') : (
              <>
                <ChevronRight className="h-4 w-4 mr-1" />
                {isEdit ? 'Update Invoice & PDF' : 'Generate Invoice & PDF'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
